import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    Shield, LogOut, Search, Check, X, Crown, Ban, RefreshCw, Trash2,
    QrCode, Instagram, Phone, Calendar, Clock, TrendingUp, ScanLine, Printer, Users, UserPlus, BookUser, CalendarClock, MessageSquare, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useT } from "../lib/i18n";
import { api, photoUrl } from "../lib/api";
import LanguageToggle from "../components/LanguageToggle";
import AdminInstallPrompt from "../components/AdminInstallPrompt";
import AddMemberModal from "../components/AddMemberModal";
import SystemStatus from "../components/SystemStatus";

const STATUS_STYLE = {
    pending: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    approved: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    vip: "bg-gold/15 text-gold border-gold/40",
    rejected: "bg-red-900/30 text-red-300 border-red-700/40",
    blacklist: "bg-zinc-700/30 text-zinc-300 border-zinc-600/40",
};

function StatusPill({ status, vip }) {
    const key = vip && status === "approved" ? "vip" : status;
    return (
        <span className={`inline-flex items-center px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase border rounded-sm ${STATUS_STYLE[key] || STATUS_STYLE.pending}`}>
            {key}
        </span>
    );
}

function StatCard({ label, value, accent, testid }) {
    return (
        <div className={`relative rounded-sm border p-5 bg-bg-secondary/70 ${accent ? "border-gold/40" : "border-emerald-line/60"}`} data-testid={testid}>
            <div className="text-[10px] tracking-[0.28em] uppercase text-ink-muted">{label}</div>
            <div className={`font-serif text-4xl mt-2 ${accent ? "gold-text" : "text-ink-primary"}`}>{value}</div>
        </div>
    );
}

function AdminEditPanel({ selected, setSelected, reloadList, t }) {
    const split = (selected.full_name || "").split(" ");
    const [firstName, setFirstName] = useState(selected.first_name || split[0] || "");
    const [lastName, setLastName] = useState(selected.last_name || split.slice(1).join(" ") || "");
    const [instagram, setInstagram] = useState(selected.instagram || "");
    const [phone, setPhone] = useState(selected.phone || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const s2 = (selected.full_name || "").split(" ");
        setFirstName(selected.first_name || s2[0] || "");
        setLastName(selected.last_name || s2.slice(1).join(" ") || "");
        setInstagram(selected.instagram || "");
        setPhone(selected.phone || "");
    }, [selected.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/applications/${selected.id}`, {
                first_name: firstName, last_name: lastName, instagram, phone,
            });
            setSelected(data);
            reloadList();
            toast.success(t("profile.saved"));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="rounded-sm border border-emerald-line/60 bg-bg-primary/40 p-4 space-y-3" data-testid="admin-edit-panel">
            <div className="label-eyebrow">{t("profile.edit")}</div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="text-[10px] tracking-[0.22em] uppercase text-ink-muted">{t("profile.first_name")}</label>
                    <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="input-line" data-testid="edit-first-name" />
                </div>
                <div>
                    <label className="text-[10px] tracking-[0.22em] uppercase text-ink-muted">{t("profile.last_name")}</label>
                    <input value={lastName} onChange={(e) => setLastName(e.target.value)} className="input-line" data-testid="edit-last-name" />
                </div>
                <div>
                    <label className="text-[10px] tracking-[0.22em] uppercase text-ink-muted">Instagram</label>
                    <input value={instagram} onChange={(e) => setInstagram(e.target.value)} className="input-line" data-testid="edit-instagram" />
                </div>
                <div>
                    <label className="text-[10px] tracking-[0.22em] uppercase text-ink-muted">Phone</label>
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} className="input-line" data-testid="edit-phone" />
                </div>
            </div>
            <button
                onClick={save}
                disabled={saving}
                data-testid="admin-save-profile"
                className="inline-flex items-center gap-2 px-4 py-2 border border-gold/40 text-gold rounded-sm hover:bg-gold/10 text-xs tracking-[0.22em] uppercase disabled:opacity-50"
            >
                {saving ? t("common.saving") : t("profile.save")}
            </button>
        </div>
    );
}

export default function AdminDashboard() {
    const { t } = useT();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [occupancy, setOccupancy] = useState(null);
    const [tab, setTab] = useState("pending");
    const [showAdd, setShowAdd] = useState(false);
    const [search, setSearch] = useState("");
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [notes, setNotes] = useState("");
    const [savingNotes, setSavingNotes] = useState(false);
    const adminEmail = localStorage.getItem("sc_admin_email") || "";

    const signout = useCallback(() => {
        localStorage.removeItem("sc_admin_token");
        localStorage.removeItem("sc_admin_email");
        navigate("/admin/login", { replace: true });
    }, [navigate]);

    const loadStats = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/stats");
            setStats(data);
        } catch (e) {
            if (e?.response?.status === 401) signout();
        }
    }, [signout]);

    const loadOccupancy = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/occupancy");
            setOccupancy(data);
        } catch { /* */ }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 1000 };
            if (tab !== "all") params.status = tab;
            if (search.trim().length >= 2) params.q = search.trim();
            const { data } = await api.get("/admin/applications", { params });
            setApps(data);
        } catch (e) {
            if (e?.response?.status === 401) signout();
            else toast.error("Failed to load applications");
        } finally {
            setLoading(false);
        }
    }, [tab, search, signout]);

    useEffect(() => {
        if (!localStorage.getItem("sc_admin_token")) {
            navigate("/admin/login", { replace: true });
            return;
        }
        loadStats();
        loadOccupancy();
        const id = setInterval(loadOccupancy, 10000);
        return () => clearInterval(id);
    }, [navigate, loadStats, loadOccupancy]);

    useEffect(() => {
        // Load once + refresh only when tab / search changes. No aggressive polling
        // (721+ members re-fetching every 8s was crushing the UI).
        // Longer debounce so typing in search doesn't fire a request per keystroke.
        const id = setTimeout(load, 500);
        return () => clearTimeout(id);
    }, [load]);

    const setStatus = async (app, status) => {
        try {
            await api.patch(`/admin/applications/${app.id}/status`, { status });
            toast.success(`Marked ${status}`);
            await Promise.all([load(), loadStats()]);
            if (selected?.id === app.id) {
                const { data } = await api.get(`/admin/applications/${app.id}`);
                setSelected(data);
            }
        } catch {
            toast.error("Update failed");
        }
    };

    const toggleVip = async (app) => {
        try {
            await api.patch(`/admin/applications/${app.id}/vip`, { vip: !app.vip });
            toast.success(!app.vip ? "Tagged VIP" : "VIP removed");
            await Promise.all([load(), loadStats()]);
            if (selected?.id === app.id) {
                const { data } = await api.get(`/admin/applications/${app.id}`);
                setSelected(data);
            }
        } catch {
            toast.error("VIP update failed");
        }
    };

    const checkIn = async (app) => {
        try {
            const { data } = await api.post(`/admin/applications/${app.id}/check-in`);
            if (data?.duplicate) toast(t("occ.duplicate"));
            else if (data?.already_inside) toast(t("occ.already_inside"));
            else toast.success("Checked in");
            await Promise.all([load(), loadStats(), loadOccupancy()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Check-in failed");
        }
    };

    const checkOut = async (app) => {
        try {
            await api.post(`/admin/applications/${app.id}/check-out`);
            toast.success(t("occ.checked_out"));
            await Promise.all([load(), loadStats(), loadOccupancy()]);
            if (selected?.id === app.id) {
                const { data } = await api.get(`/admin/applications/${app.id}`);
                setSelected(data);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Check-out failed");
        }
    };

    const remove = async (app) => {
        if (!window.confirm(t("admin.confirm_delete"))) return;
        try {
            await api.delete(`/admin/applications/${app.id}`);
            toast.success("Deleted");
            setSelected(null);
            await Promise.all([load(), loadStats()]);
        } catch {
            toast.error("Delete failed");
        }
    };

    const saveNotes = async () => {
        if (!selected) return;
        setSavingNotes(true);
        try {
            await api.patch(`/admin/applications/${selected.id}/notes`, { admin_notes: notes });
            toast.success(t("common.saved"));
            const { data } = await api.get(`/admin/applications/${selected.id}`);
            setSelected(data);
        } catch {
            toast.error("Save failed");
        } finally {
            setSavingNotes(false);
        }
    };

    const openDetail = (app) => {
        setSelected(app);
        setNotes(app.admin_notes || "");
    };

    const tabs = useMemo(() => ([
        { key: "pending", label: t("admin.tabs.pending") },
        { key: "approved", label: t("admin.tabs.approved") },
        { key: "vip", label: "VIP" },
        { key: "rejected", label: t("admin.tabs.rejected") },
        { key: "blacklist", label: t("admin.tabs.blacklist") },
        { key: "all", label: t("admin.tabs.all") },
    ]), [t]);

    return (
        <div className="min-h-[100dvh] text-ink-primary" data-testid="admin-dashboard">
            {/* Top bar */}
            <header className="sticky top-0 z-30 glass-strong border-b border-emerald-line/60">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Shield size={16} className="text-gold" />
                        <span className="font-serif text-lg tracking-wide">ScottsClub Admin</span>
                    </div>
                    <div className="flex items-center gap-6">
                        <LanguageToggle />
                        <Link
                            to="/scan"
                            data-testid="nav-scan"
                            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-secondary hover:text-gold"
                        >
                            <ScanLine size={14} /> Scan
                        </Link>
                        <Link
                            to="/admin/door-staff"
                            data-testid="nav-door-staff"
                            className="hidden sm:inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-secondary hover:text-gold"
                        >
                            <Users size={14} /> Staff
                        </Link>
                        <Link
                            to="/admin/directory"
                            data-testid="nav-directory"
                            className="hidden sm:inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-secondary hover:text-gold"
                        >
                            <BookUser size={14} /> Directory
                        </Link>
                        <button
                            onClick={() => setShowAdd(true)}
                            data-testid="nav-add-member"
                            className="inline-flex items-center gap-2 px-3 py-1.5 border border-gold/40 text-gold rounded-sm hover:bg-gold/10 text-[11px] tracking-[0.22em] uppercase"
                        >
                            <UserPlus size={13} /> Add
                        </button>
                        <Link
                            to="/admin/posters"
                            data-testid="nav-posters"
                            className="hidden sm:inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-secondary hover:text-gold"
                        >
                            <Printer size={14} /> Posters
                        </Link>
                        <Link
                            to="/admin/event"
                            data-testid="nav-event"
                            className="hidden sm:inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-secondary hover:text-gold"
                        >
                            <CalendarClock size={14} /> Event
                        </Link>
                        <Link
                            to="/admin/broadcast"
                            data-testid="nav-broadcast"
                            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-secondary hover:text-gold"
                        >
                            <MessageSquare size={14} /> SMS
                        </Link>
                        <Link
                            to="/admin/banner"
                            data-testid="nav-banner"
                            className="hidden sm:inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-secondary hover:text-gold"
                        >
                            <ImageIcon size={14} /> Banner
                        </Link>
                        <div className="hidden sm:block text-xs text-ink-secondary">
                            {t("admin.signed_in_as")} <span className="text-gold">{adminEmail}</span>
                        </div>
                        <button
                            onClick={signout}
                            data-testid="admin-signout"
                            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-secondary hover:text-gold"
                        >
                            <LogOut size={14} /> {t("admin.signout")}
                        </button>
                    </div>
                </div>
                <div className="gold-divider opacity-30" />
            </header>

            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
                {/* System status — SMS + domain */}
                <SystemStatus />

                {/* Live occupancy */}
                <section className="mb-10" data-testid="occupancy-section">
                    <div className="label-eyebrow">{t("occ.who_inside")}</div>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="rounded-sm border border-gold/40 bg-bg-secondary/70 p-5">
                            <div className="text-[10px] tracking-[0.28em] uppercase text-ink-muted">{t("occ.inside_now")}</div>
                            <div className="font-serif text-5xl gold-text mt-2" data-testid="admin-inside-now">{occupancy?.inside_now ?? "—"}</div>
                        </div>
                        <div className="rounded-sm border border-emerald-line/60 bg-bg-secondary/70 p-5">
                            <div className="text-[10px] tracking-[0.28em] uppercase text-ink-muted">{t("occ.entries_tonight")}</div>
                            <div className="font-serif text-5xl text-ink-primary mt-2" data-testid="admin-entries-tonight">{occupancy?.entries_tonight ?? "—"}</div>
                        </div>
                        <div className="rounded-sm border border-emerald-line/60 bg-bg-secondary/70 p-5 md:col-span-1">
                            <div className="text-[10px] tracking-[0.28em] uppercase text-ink-muted mb-3">{t("occ.who_inside")}</div>
                            {occupancy?.inside?.length ? (
                                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                                    {occupancy.inside.slice(0, 30).map((m) => (
                                        <button
                                            key={m.id}
                                            onClick={() => checkOut(m)}
                                            title={t("occ.check_out")}
                                            data-testid={`inside-${m.id}`}
                                            className="group inline-flex items-center gap-2 px-2 py-1 rounded-sm bg-bg-surface/80 border border-emerald-line/60 hover:border-gold/50"
                                        >
                                            <div className="w-6 h-6 rounded-full overflow-hidden bg-bg-surface">
                                                {m.photo_id && <img src={photoUrl(m.photo_id)} alt="" className="w-full h-full object-cover" />}
                                            </div>
                                            <span className="text-xs">{m.full_name?.split(" ")[0] || m.full_name}</span>
                                            {m.vip && <Crown size={10} className="text-gold" />}
                                            <X size={11} className="opacity-0 group-hover:opacity-100 text-ink-muted" />
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-sm text-ink-muted">{t("occ.no_one")}</div>
                            )}
                        </div>
                    </div>
                </section>

                {/* Stats */}
                <section>
                    <div className="label-eyebrow">{t("admin.stats.title")}</div>
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <StatCard testid="stat-pending" label={t("admin.stats.pending")} value={stats?.pending ?? "—"} accent />
                        <StatCard testid="stat-approved" label={t("admin.stats.approved")} value={stats?.approved ?? "—"} />
                        <StatCard testid="stat-vip" label={t("admin.stats.vip")} value={stats?.vip ?? "—"} />
                        <StatCard testid="stat-rejected" label={t("admin.stats.rejected")} value={stats?.rejected ?? "—"} />
                        <StatCard testid="stat-blacklist" label={t("admin.stats.blacklisted")} value={stats?.blacklisted ?? "—"} />
                        <StatCard testid="stat-week" label={t("admin.stats.last_week")} value={stats?.last_week ?? "—"} />
                    </div>

                    {/* Most active */}
                    <div className="mt-6 rounded-sm border border-emerald-line/60 bg-bg-secondary/60 p-5">
                        <div className="flex items-center gap-2 text-ink-secondary">
                            <TrendingUp size={14} className="text-gold" />
                            <span className="label-eyebrow !text-gold">{t("admin.stats.top")}</span>
                        </div>
                        <div className="mt-4">
                            {stats?.top_members?.length ? (
                                <div className="flex flex-wrap gap-3">
                                    {stats.top_members.map((m) => (
                                        <div key={m.id} className="flex items-center gap-3 px-3 py-2 rounded-sm border border-emerald-line/60">
                                            <div className="w-8 h-8 rounded-full overflow-hidden bg-bg-surface">
                                                {m.photo_id && <img src={photoUrl(m.photo_id)} alt="" className="w-full h-full object-cover" />}
                                            </div>
                                            <div>
                                                <div className="text-sm">{m.full_name}</div>
                                                <div className="text-[10px] tracking-widest uppercase text-ink-muted">@{m.instagram} · {m.check_ins} {t("vip.check_ins").toLowerCase()}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-ink-muted text-sm">{t("admin.stats.no_top")}</div>
                            )}
                        </div>
                    </div>
                </section>

                {/* Tabs + search */}
                <section className="mt-12">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex flex-wrap gap-1 border border-emerald-line/60 rounded-sm p-1 bg-bg-secondary/40">
                            {tabs.map((tb) => (
                                <button
                                    key={tb.key}
                                    onClick={() => setTab(tb.key)}
                                    data-testid={`tab-${tb.key}`}
                                    className={`px-3.5 py-1.5 text-[11px] tracking-[0.22em] uppercase rounded-sm transition-colors ${
                                        tab === tb.key ? "bg-gold text-black" : "text-ink-secondary hover:text-gold"
                                    }`}
                                >{tb.label}</button>
                            ))}
                        </div>
                        <div className="relative md:w-80 flex items-center gap-2">
                            <div className="relative flex-1">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder={t("admin.search_ph")}
                                    data-testid="admin-search"
                                    className="w-full pl-9 pr-3 py-2 bg-bg-secondary/70 border border-emerald-line/60 rounded-sm text-sm focus:border-gold/60 outline-none"
                                />
                            </div>
                            <button
                                onClick={() => { load(); loadStats(); }}
                                disabled={loading}
                                data-testid="admin-refresh"
                                title="Refresh list"
                                className="p-2 border border-emerald-line/60 hover:border-gold/50 hover:text-gold text-ink-secondary rounded-sm transition-colors disabled:opacity-40"
                            >
                                <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                            </button>
                        </div>
                    </div>

                    {/* List */}
                    <div className="mt-6 rounded-sm border border-emerald-line/60 overflow-hidden bg-bg-secondary/40">
                        {loading && apps.length === 0 ? (
                            <div className="p-10 text-center text-ink-muted text-sm">Loading…</div>
                        ) : apps.length === 0 ? (
                            <div className="p-10 text-center text-ink-muted text-sm">{t("admin.no_results")}</div>
                        ) : (
                            <ul className="divide-y divide-emerald-line/60" data-testid="applications-list">
                                {apps.map((a) => (
                                    <li
                                        key={a.id}
                                        className="flex flex-col md:flex-row md:items-center gap-4 px-4 md:px-6 py-4 hover:bg-bg-surface/50 transition-colors cursor-pointer"
                                        onClick={() => openDetail(a)}
                                        data-testid={`app-row-${a.id}`}
                                    >
                                        <div className="flex items-center gap-4 flex-1 min-w-0">
                                            <div className="w-12 h-12 rounded-full overflow-hidden bg-bg-surface border border-emerald-line/60 flex-shrink-0">
                                                {a.photo_id && <img src={photoUrl(a.photo_id)} alt={a.full_name} className="w-full h-full object-cover" />}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <div className="font-medium text-ink-primary truncate">{a.full_name}</div>
                                                    {a.vip && <Crown size={14} className="text-gold flex-shrink-0" />}
                                                </div>
                                                <div className="text-xs text-ink-secondary mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                                                    <span className="inline-flex items-center gap-1"><Instagram size={11} />@{a.instagram}</span>
                                                    <span className="inline-flex items-center gap-1"><Phone size={11} />{a.phone}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                                            <span className="text-xs text-ink-muted hidden lg:inline-flex items-center gap-1 mr-1">
                                                <Clock size={11} />{new Date(a.created_at).toLocaleDateString()}
                                            </span>
                                            <StatusPill status={a.status} vip={a.vip} />

                                            {/* Inline quick actions */}
                                            {a.status !== "approved" && a.status !== "vip" && (
                                                <button
                                                    onClick={() => setStatus(a, "approved")}
                                                    data-testid={`quick-approve-${a.id}`}
                                                    title={t("admin.actions.approve")}
                                                    className="px-2.5 py-1.5 bg-gold/15 text-gold border border-gold/40 rounded-sm hover:bg-gold/25 text-[10px] tracking-[0.18em] uppercase inline-flex items-center gap-1.5"
                                                >
                                                    <Check size={12} /> {t("admin.actions.approve")}
                                                </button>
                                            )}
                                            {a.status !== "rejected" && (
                                                <button
                                                    onClick={() => setStatus(a, "rejected")}
                                                    data-testid={`quick-reject-${a.id}`}
                                                    title={t("admin.actions.reject")}
                                                    className="px-2.5 py-1.5 border border-red-700/40 text-red-300 rounded-sm hover:bg-red-700/10 text-[10px] tracking-[0.18em] uppercase inline-flex items-center gap-1.5"
                                                >
                                                    <X size={12} /> {t("admin.actions.reject")}
                                                </button>
                                            )}
                                            {(a.status === "approved" || a.status === "vip") && (
                                                <button
                                                    onClick={() => toggleVip(a)}
                                                    data-testid={`quick-vip-${a.id}`}
                                                    title={a.vip ? t("admin.actions.unvip") : t("admin.actions.vip")}
                                                    className="px-2.5 py-1.5 border border-gold/40 text-gold rounded-sm hover:bg-gold/10 text-[10px] tracking-[0.18em] uppercase inline-flex items-center gap-1.5"
                                                >
                                                    <Crown size={12} /> {a.vip ? "VIP" : t("admin.actions.vip")}
                                                </button>
                                            )}
                                            {a.status !== "blacklist" ? (
                                                <button
                                                    onClick={() => setStatus(a, "blacklist")}
                                                    data-testid={`quick-blacklist-${a.id}`}
                                                    title={t("admin.actions.ban")}
                                                    className="px-2.5 py-1.5 border border-zinc-600/40 text-zinc-300 rounded-sm hover:bg-zinc-600/10 text-[10px] tracking-[0.18em] uppercase inline-flex items-center gap-1.5"
                                                >
                                                    <Ban size={12} /> {t("admin.actions.ban")}
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => setStatus(a, "pending")}
                                                    data-testid={`quick-restore-${a.id}`}
                                                    title={t("admin.actions.restore")}
                                                    className="px-2.5 py-1.5 border border-emerald-line/60 text-ink-primary rounded-sm hover:bg-bg-surface text-[10px] tracking-[0.18em] uppercase inline-flex items-center gap-1.5"
                                                >
                                                    <RefreshCw size={12} /> {t("admin.actions.restore")}
                                                </button>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>
            </main>

            {/* Detail Drawer */}
            {selected && (
                <div className="fixed inset-0 z-50 flex" data-testid="detail-drawer">
                    <button
                        aria-label="close"
                        className="flex-1 bg-black/70 backdrop-blur-sm"
                        onClick={() => setSelected(null)}
                    />
                    <motion.aside
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="w-full max-w-md bg-bg-secondary border-l border-emerald-line/60 h-full overflow-y-auto"
                    >
                        <div className="sticky top-0 glass-strong border-b border-emerald-line/60 px-6 py-4 flex items-center justify-between">
                            <div>
                                <div className="label-eyebrow">{t("admin.detail_title")}</div>
                                <div className="font-serif text-xl mt-1">{selected.full_name}</div>
                            </div>
                            <button onClick={() => setSelected(null)} data-testid="close-detail" className="text-ink-secondary hover:text-gold">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="px-6 py-6 space-y-6">
                            {selected.photo_id && (
                                <div className="relative aspect-[4/3] w-full rounded-sm overflow-hidden border border-emerald-line/60">
                                    <img src={photoUrl(selected.photo_id)} alt={selected.full_name} className="w-full h-full object-cover" />
                                    <label className="absolute bottom-3 right-3 px-3 py-1.5 rounded-sm glass-strong text-[10px] tracking-[0.2em] uppercase text-gold hover:bg-gold/10 cursor-pointer" data-testid="admin-replace-photo">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={async (e) => {
                                                const f = e.target.files?.[0];
                                                if (!f) return;
                                                const fd = new FormData();
                                                fd.append("photo", f);
                                                try {
                                                    await api.post(`/admin/applications/${selected.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                                                    toast.success("Photo updated");
                                                    const { data } = await api.get(`/admin/applications/${selected.id}`);
                                                    setSelected(data);
                                                    load();
                                                } catch (err) {
                                                    toast.error(err?.response?.data?.detail || "Upload failed");
                                                }
                                            }}
                                        />
                                        {t("profile.change_photo")}
                                    </label>
                                </div>
                            )}

                            <AdminEditPanel selected={selected} setSelected={setSelected} reloadList={load} t={t} />

                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="label-eyebrow mb-1">Instagram</div>
                                    <div className="text-ink-primary">@{selected.instagram}</div>
                                </div>
                                <div>
                                    <div className="label-eyebrow mb-1">Phone</div>
                                    <div className="text-ink-primary">{selected.phone}</div>
                                </div>
                                <div>
                                    <div className="label-eyebrow mb-1">Applied</div>
                                    <div className="text-ink-primary">{new Date(selected.created_at).toLocaleString()}</div>
                                </div>
                                <div>
                                    <div className="label-eyebrow mb-1">Status</div>
                                    <StatusPill status={selected.status} vip={selected.vip} />
                                </div>
                                <div className="col-span-2">
                                    <div className="label-eyebrow mb-1">Check-ins</div>
                                    <div className="text-ink-primary">{selected.check_ins} · last: {selected.last_check_in ? new Date(selected.last_check_in).toLocaleString() : "—"}</div>
                                </div>
                            </div>

                            {selected.note && (
                                <div>
                                    <div className="label-eyebrow mb-1">Applicant note</div>
                                    <p className="text-ink-secondary text-sm leading-relaxed">{selected.note}</p>
                                </div>
                            )}

                            <div>
                                <div className="label-eyebrow mb-2">{t("admin.notes_label")}</div>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder={t("admin.notes_ph")}
                                    rows={4}
                                    className="w-full p-3 rounded-sm bg-bg-primary/60 border border-emerald-line/60 focus:border-gold/60 outline-none text-sm"
                                    data-testid="notes-textarea"
                                />
                                <button
                                    onClick={saveNotes}
                                    disabled={savingNotes}
                                    data-testid="save-notes"
                                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 border border-gold/40 text-gold rounded-sm hover:bg-gold/10 text-xs tracking-[0.22em] uppercase disabled:opacity-50"
                                >
                                    {savingNotes ? t("common.saving") : t("admin.actions.save_notes")}
                                </button>
                            </div>

                            {/* Action buttons */}
                            <div className="space-y-2 pt-2 border-t border-emerald-line/60">
                                {selected.status !== "approved" && selected.status !== "vip" && (
                                    <button
                                        onClick={() => setStatus(selected, "approved")}
                                        data-testid="action-approve"
                                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-gold text-black rounded-sm font-medium hover:bg-gold-bright"
                                    >
                                        <Check size={16} /> {t("admin.actions.approve")}
                                    </button>
                                )}
                                {selected.status !== "rejected" && (
                                    <button
                                        onClick={() => setStatus(selected, "rejected")}
                                        data-testid="action-reject"
                                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-red-700/40 text-red-300 rounded-sm hover:bg-red-700/10"
                                    >
                                        <X size={16} /> {t("admin.actions.reject")}
                                    </button>
                                )}
                                {(selected.status === "approved" || selected.status === "vip") && (
                                    <>
                                        <button
                                            onClick={() => toggleVip(selected)}
                                            data-testid="action-vip"
                                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-gold/40 text-gold rounded-sm hover:bg-gold/10"
                                        >
                                            <Crown size={16} /> {selected.vip ? t("admin.actions.unvip") : t("admin.actions.vip")}
                                        </button>
                                        {selected.currently_inside ? (
                                            <button
                                                onClick={() => checkOut(selected)}
                                                data-testid="action-checkout"
                                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-emerald-400/60 text-emerald-200 rounded-sm hover:bg-emerald-400/10"
                                            >
                                                <X size={16} /> {t("occ.check_out")}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => checkIn(selected)}
                                                data-testid="action-checkin"
                                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-emerald-500/40 text-emerald-300 rounded-sm hover:bg-emerald-500/10"
                                            >
                                                <QrCode size={16} /> {t("admin.actions.check_in")}
                                            </button>
                                        )}
                                    </>
                                )}
                                {selected.status !== "blacklist" ? (
                                    <button
                                        onClick={() => setStatus(selected, "blacklist")}
                                        data-testid="action-blacklist"
                                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-zinc-600/40 text-zinc-300 rounded-sm hover:bg-zinc-600/10"
                                    >
                                        <Ban size={16} /> {t("admin.actions.ban")}
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => setStatus(selected, "pending")}
                                        data-testid="action-restore"
                                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-emerald-line/60 text-ink-primary rounded-sm hover:bg-bg-surface"
                                    >
                                        <RefreshCw size={16} /> {t("admin.actions.restore")}
                                    </button>
                                )}
                                <button
                                    onClick={() => remove(selected)}
                                    data-testid="action-delete"
                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-red-400 hover:text-red-300 text-xs tracking-[0.22em] uppercase"
                                >
                                    <Trash2 size={14} /> {t("admin.actions.delete")}
                                </button>
                            </div>
                        </div>
                    </motion.aside>
                </div>
            )}
            <AdminInstallPrompt />
            <AddMemberModal
                open={showAdd}
                onClose={() => setShowAdd(false)}
                onCreated={() => { load(); loadStats(); loadOccupancy(); }}
            />
        </div>
    );
}
